import type { DeliveryDispatchStatus } from '@/lib/delivery-providers-client';

/**
 * How a dispatch status reads at a glance. Explicit light/dark pairs, like the board's payment pill:
 * a tint that only works in one mode reads as broken.
 */
export type DeliveryTone = 'waiting' | 'active' | 'moving' | 'done' | 'problem' | 'closed';

const TONE_BY_STATUS: Record<DeliveryDispatchStatus, DeliveryTone> = {
  requested: 'waiting',
  on_hold: 'waiting',
  searching: 'waiting',
  assigned: 'active',
  at_pickup: 'active',
  on_the_way: 'moving',
  at_dropoff: 'moving',
  delivered: 'done',
  no_courier: 'problem',
  cancelled: 'problem',
  failed: 'problem',
  error: 'problem',
};

export const DELIVERY_TONE_CLASSES: Record<DeliveryTone, string> = {
  waiting:
    'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/40 dark:text-amber-300',
  active: 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-800/60 dark:bg-sky-950/40 dark:text-sky-300',
  moving:
    'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800/60 dark:bg-violet-950/40 dark:text-violet-300',
  done: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-950/40 dark:text-emerald-300',
  closed: 'border-transparent bg-muted text-muted-foreground',
  problem: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800/60 dark:bg-red-950/40 dark:text-red-300',
};

/**
 * `attention: false` on an ended run (the business cancelled it itself) reads as closed, not as a
 * problem — see `dispatchNeedsAttention`.
 */
export function deliveryToneOf(status: DeliveryDispatchStatus, attention?: boolean): DeliveryTone {
  const tone = TONE_BY_STATUS[status] ?? 'waiting';
  return tone === 'problem' && attention === false ? 'closed' : tone;
}

/** `orders` locale key of a status's label. */
export function deliveryStatusKey(status: DeliveryDispatchStatus): string {
  return `delivery.status.${status}`;
}
